import axios from "axios";

import createAuthRefreshInterceptor
  from "axios-auth-refresh";

import Cookies from "js-cookie";

import {
  store,
} from "../store/index.ts";

import {
  logout,
  setAccessToken,
} from "../store/slices/auth.ts";

import {
  config,
} from "./constants";


const REACT_APP_API_URL =
  config.url.REACT_APP_API_URL;


/*
 * Axios instance used for authenticated API requests.
 */
const axiosService = axios.create({
  baseURL: REACT_APP_API_URL,

  withCredentials: true,

  headers: {
    "Content-Type":
      "application/json",
  },
});


/*
 * Add the current Redux access token to every API request.
 *
 * This runs immediately before each request, so a newly
 * refreshed access token is automatically used.
 */
axiosService.interceptors.request.use(
  (requestConfig) => {
    const accessToken =
      store.getState()
        .auth
        .accessToken;

    if (accessToken) {
      requestConfig.headers.Authorization =
        `Bearer ${accessToken}`;
    } else {
      delete requestConfig
        .headers
        .Authorization;
    }

    const csrfToken =
      Cookies.get(
        "csrftoken"
      );

    if (csrfToken) {
      requestConfig.headers[
        "X-CSRFToken"
      ] = csrfToken;
    } else {
      delete requestConfig.headers[
        "X-CSRFToken"
      ];
    }

    return requestConfig;
  },

  (error) =>
    Promise.reject(error)
);


/*
 * Called when an authenticated request returns 401.
 *
 * An expired access token does NOT immediately log the
 * customer out.
 *
 * The refresh-token cookie is tried first.
 */
const refreshAuthLogic = async (
  failedRequest: any
) => {
  /*
   * Do not use the still-existing HttpOnly refresh cookie
   * after the customer deliberately clicked Logout.
   */
  const authState =
    store.getState().auth;

  if (!authState.isAuthenticated) {
    return Promise.reject(
      new Error(
        "The user is logged out."
      )
    );
  }

  try {
    const csrfToken =
      Cookies.get(
        "csrftoken"
      );

    /*
     * Use plain axios for the refresh request so the
     * refresh call itself does not go through this
     * axiosService refresh interceptor.
     *
     * The HttpOnly refresh token is automatically sent
     * by the browser because withCredentials is true.
     */
    const response =
      await axios.post(
        `${REACT_APP_API_URL}/auth/refresh/`,
        {},
        {
          withCredentials: true,

          headers: {
            "Content-Type":
              "application/json",

            ...(csrfToken
              ? {
                  "X-CSRFToken":
                    csrfToken,
                }
              : {}),
          },
        }
      );

    const newAccessToken =
      response.data?.access;

    if (!newAccessToken) {
      throw new Error(
        "Refresh response did not contain an access token."
      );
    }

    /*
     * Store the new five-minute access token in Redux.
     */
    store.dispatch(
      setAccessToken(
        newAccessToken
      )
    );

    /*
     * Update the request that originally failed.
     *
     * axios-auth-refresh retries it after this refresh
     * promise resolves.
     */
    if (
      failedRequest
        ?.response
        ?.config
    ) {
      failedRequest
        .response
        .config
        .headers =
          failedRequest
            .response
            .config
            .headers || {};

      failedRequest
        .response
        .config
        .headers
        .Authorization =
          `Bearer ${newAccessToken}`;
    }

    return Promise.resolve();
  } catch (error) {
    /*
     * Refresh itself failed.
     *
     * At this point the refresh token is missing,
     * expired, invalid, or otherwise unusable.
     * Now the frontend session should end.
     */
    console.error(
      "Unable to refresh authentication:",
      error
    );

    store.dispatch(
      logout()
    );

    return Promise.reject(
      error
    );
  }
};


/*
 * A 401 from axiosService triggers refreshAuthLogic.
 *
 * Additional requests are held while the refresh is
 * occurring and continue after it resolves.
 */
createAuthRefreshInterceptor(
  axiosService,
  refreshAuthLogic,
  {
    statusCodes: [
      401,
    ],
  }
);


export function fetcher<T = any>(
  url: string
): Promise<T> {
  return axiosService
    .get<T>(url)
    .then(
      (response) =>
        response.data
    );
}


export default axiosService;